// HtmlInputAttributes.jsx
import React from "react";

const HtmlInputAttributes = () => {
  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", lineHeight: "1.6" }}>
      <h1>HTML Input Attributes - Comprehensive Guide</h1>

      {/* Introduction */}
      <section id="introduction">
        <h2>1. Introduction</h2>
        <p>
          HTML input attributes enhance form inputs by controlling behavior, validation, accessibility, and styling.
          Using them correctly ensures better user experience and data accuracy.
        </p>
      </section>

      {/* Basic Attributes */}
      <section id="basic-attributes">
        <h2>2. Basic Attributes</h2>
        <p>Common attributes available for most input types:</p>
        <ul>
          <li><strong>type:</strong> Specifies the type of input (text, email, number, etc.)</li>
          <li><strong>name:</strong> Identifies input data when submitted</li>
          <li><strong>id:</strong> Unique identifier for label association</li>
          <li><strong>value:</strong> Default or pre-filled value</li>
          <li><strong>placeholder:</strong> Hint text shown inside input</li>
          <li><strong>disabled:</strong> Makes the input non-editable</li>
          <li><strong>readonly:</strong> Read-only input (user can’t modify)</li>
        </ul>
        <pre>
          <code>
{`<label htmlFor="username">Username:</label>
<input type="text" id="username" name="username" placeholder="Enter username" value="JohnDoe" />`}
          </code>
        </pre>
      </section>

      {/* Validation Attributes */}
      <section id="validation-attributes">
        <h2>3. Validation Attributes</h2>
        <p>Attributes that help validate user input:</p>
        <ul>
          <li><strong>required:</strong> Must fill before submitting</li>
          <li><strong>min / max:</strong> Specify numeric or date limits</li>
          <li><strong>minlength / maxlength:</strong> Limit character length</li>
          <li><strong>pattern:</strong> Regular expression for custom validation</li>
          <li><strong>step:</strong> Defines increments for number or date</li>
        </ul>
        <pre>
          <code>
{`<label htmlFor="age">Age (18-60):</label>
<input type="number" id="age" name="age" min="18" max="60" required />

<label htmlFor="username">Username (4-10 chars):</label>
<input type="text" id="username" name="username" minlength="4" maxlength="10" required />

<label htmlFor="email">Email:</label>
<input type="email" id="email" name="email" pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,}$" required />`}
          </code>
        </pre>
      </section>

      {/* Accessibility Attributes */}
      <section id="accessibility-attributes">
        <h2>4. Accessibility Attributes</h2>
        <p>Attributes that improve accessibility:</p>
        <ul>
          <li><strong>aria-label:</strong> Provides label for screen readers</li>
          <li><strong>aria-describedby:</strong> Links input to description text</li>
          <li><strong>title:</strong> Tooltip shown on hover</li>
        </ul>
        <pre>
          <code>
{`<label htmlFor="phone">Phone:</label>
<input type="tel" id="phone" name="phone" aria-label="Phone number" title="Enter your phone number" /> 

<p id="desc">Format: +1234567890</p>
<input type="tel" id="phone2" aria-describedby="desc" />`}
          </code>
        </pre>
      </section>

      {/* Event Attributes */}
      <section id="event-attributes">
        <h2>5. Event Attributes</h2>
        <p>Attributes to handle user interactions:</p>
        <ul>
          <li><strong>onChange:</strong> Triggered when input value changes</li>
          <li><strong>onFocus:</strong> Triggered when input gets focus</li>
          <li><strong>onBlur:</strong> Triggered when input loses focus</li>
          <li><strong>onInput:</strong> Triggered while typing</li>
        </ul>
        <pre>
          <code>
{`<input 
  type="text" 
  onChange={(e) => console.log("Changed:", e.target.value)} 
  onFocus={() => console.log("Focused")} 
  onBlur={() => console.log("Blurred")} 
/>`}
          </code>
        </pre>
      </section>

      {/* Other Useful Attributes */}
      <section id="other-attributes">
        <h2>6. Other Useful Attributes</h2>
        <ul>
          <li><strong>autocomplete:</strong> Enables or disables autocomplete</li>
          <li><strong>autofocus:</strong> Focus input automatically on page load</li>
          <li><strong>multiple:</strong> Allows multiple file selection</li>
          <li><strong>list:</strong> Connects input to &lt;datalist&gt; for suggestions</li>
          <li><strong>form:</strong> Associates input with a specific form</li>
        </ul>
        <pre>
          <code>
{`<input type="text" name="city" list="cities" autocomplete="on" autofocus />
<datalist id="cities">
  <option value="New York" />
  <option value="London" />
  <option value="Tokyo" />
</datalist>

<input type="file" name="resume" multiple />`}
          </code>
        </pre>
      </section>

      {/* Best Practices */}
      <section id="best-practices">
        <h2>7. Best Practices</h2>
        <ul>
          <li>Always use <strong>name</strong> and <strong>id</strong> for form submission and accessibility.</li>
          <li>Use validation attributes to reduce errors and improve UX.</li>
          <li>Provide clear placeholder, title, and labels for guidance.</li>
          <li>Group inputs logically using <strong>fieldset</strong> and <strong>legend</strong>.</li>
          <li>Combine event handlers for interactivity when needed.</li>
        </ul>
      </section>
    </div>
  );
};

export default HtmlInputAttributes;
